import { useState } from "react";

import FlightIcon from "@mui/icons-material/Flight";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { GridCloseIcon } from "@mui/x-data-grid";

import { ROUTE_NUMBER_COUNT, siteName } from "@/constants/config";
import { MAX_FLIGHT_RANGE } from "@/constants/membership";
import {
  SHOW_LOYALTY_PROGRAM,
  SHOW_PANORAMA_PROGRAM,
  SHOW_PROGRAM_NAME_MAP,
} from "@/constants/programs";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { renderProgramsLogo } from "@/utils/commonRender";

const Highlights = () => {
  const theme = useTheme();
  // join loyalty program with coma
  const [searchVisible, setSearchVisible] = useState(false);
  const [routeVisible, setRouteVisible] = useState(false);
  const loyaltyProgram = SHOW_LOYALTY_PROGRAM.join(", ");

  const { isDarkMode } = useThemeData();
  const { isPc } = usePlatform();
  const routeLoyalProgram = SHOW_PANORAMA_PROGRAM.join(", ");

  const programsList = Array.from(SHOW_PROGRAM_NAME_MAP.keys()).map((program) => ({
    code: program,
    name: SHOW_PROGRAM_NAME_MAP.get(program),
  }));

  const renderProgramsList = () => {
    return (
      <>
        <DialogTitle>Programs</DialogTitle>
        <IconButton
          onClick={() => setSearchVisible(false)}
          className="!absolute !right-[8px] !top-[8px]"
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <GridCloseIcon />
        </IconButton>
        <DialogContent>
          <div
            className={
              isPc ? "grid-cols-3 grid gap-[12px] mt-[-20px]" : "flex flex-col gap-[12px]"
            }
          >
            {programsList.map((item) => {
              return (
                <div key={item.code} className="flex items-center">
                  {renderProgramsLogo(item.code, isDarkMode)}
                  <div className="ml-[10px]">{item.name}</div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </>
    );
  };
  return (
    <Box>
      <Typography
        className="flex items-center !mb-[10px]"
        color="textPrimary"
        fontWeight={20000}
        component={"div"}
      >
        <div className="flex md:mx-[5px] mx-[15px] flex-wrap items-center text-[16px] md:text-[20px] mb-[0px]">
          Award Travel: Effortlessly Planned
        </div>
      </Typography>
      <Grid container spacing={2}>
        {[
          {
            title: "Real-time Search",
            subtitle: (
              <Typography component={"div"}>
                <strong>Fastest real-time award flight search</strong> for{" "}
                <Tooltip
                  title={<div className="relative">{renderProgramsList()}</div>}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 1200,
                      },
                    },
                  }}
                  placement="bottom"
                >
                  <Typography
                    onClick={() => {
                      setSearchVisible(true);
                    }}
                    className="inline underline"
                  >
                    {SHOW_LOYALTY_PROGRAM.length} major airline loyalty programs
                  </Typography>
                </Tooltip>
                — search up to {MAX_FLIGHT_RANGE} entries at once, with results in under 25 seconds.
              </Typography>
            ),
            icon: <FlightIcon className="rotate-[45deg]" />,
          },
          {
            title: siteName + " Award Alerts",
            subtitle: (
              <Typography component={"div"}>
                <strong>Stop repeating the same search, start receiving award alerts.</strong> Set
                advanced alerts and get notified as soon as the best flights become available.
              </Typography>
            ),
            icon: <NotificationsActiveIcon />,
          },
          {
            title: "Explore Worldwide Award Flights",
            subtitle: (
              <Typography component={"div"}>
                <strong>Stop the guesswork. </strong>
                {ROUTE_NUMBER_COUNT.toLocaleString() + "+ routes "}
                are tracked continuously to make exploring the world's award flights data at
                lighting fast speed.
              </Typography>
            ),
            icon: <StarPurple500Icon />,
          },
        ].map((item, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Box width={1} height={"100%"}>
              <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                <Box
                  component={Avatar}
                  width={50}
                  height={50}
                  marginRight={2}
                  bgcolor={alpha(theme.palette.primary.main, 0.1)}
                  color={theme.palette.primary.main}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  alignSelf="center"
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    variant={"h7"}
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                    color="textPrimary"
                  >
                    {/* {item.title} */}
                    <Typography color="textSecondary" component={"div"}>
                      {item.subtitle}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Dialog
        open={searchVisible}
        maxWidth="md"
        fullScreen={!isPc}
        onClose={() => setSearchVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: isPc ? "12px" : "0px",
          },
        }}
      >
        {renderProgramsList()}
      </Dialog>
      <Dialog
        open={routeVisible}
        onClose={() => setRouteVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography fontSize={16}>{routeLoyalProgram}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Highlights;
